// IE Hub - Compute Interpreter
// Safe helpers to derive values before render/submit

import { pathGet } from '../../shared/lib/pathGet';

// Type definitions
export interface ComputeRefNode {
  var: string;
}

export interface ComputeOpNode {
  op: string;
  args: ComputeNode[];
  sep?: string;
}

export type ComputeNode = string | number | boolean | null | ComputeRefNode | ComputeOpNode | Record<string, unknown>;

export interface ComputeMap {
  [key: string]: ComputeNode;
}

export interface ComputeData {
  [key: string]: unknown;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

// Minimal compute ops: concat, join, nowISO, assign
const computeOps: Record<string, (args: unknown[], sep?: string) => unknown> = {
  concat: (args: unknown[]) => args.map((v) => (v == null ? '' : String(v))).join(''),
  join: (args: unknown[], sep = ' ') => args.map((v) => (v == null ? '' : String(v))).join(sep),
  nowISO: () => new Date().toISOString(),
};

export function resolveRef(node: ComputeNode, data: ComputeData): unknown {
  if (node === null || typeof node !== 'object') return node;
  if (isPlainObject(node) && Object.prototype.hasOwnProperty.call(node, 'var')) {
    const refNode = node as unknown as ComputeRefNode;
    const path = refNode.var;
    if (typeof path !== 'string') return undefined;
    return pathGet(data, path);
  }
  return node;
}

export function evaluateCompute(node: ComputeNode, data: ComputeData): unknown {
  if (node === null || typeof node !== 'object') return node;

  if (isPlainObject(node) && Object.prototype.hasOwnProperty.call(node, 'var')) {
    return resolveRef(node, data);
  }

  if (isPlainObject(node) && typeof (node as unknown as ComputeOpNode).op === 'string') {
    const opNode = node as unknown as ComputeOpNode;
    const op = computeOps[opNode.op];
    if (!op) return undefined;
    const args = Array.isArray(opNode.args) ? opNode.args.map((arg) => evaluateCompute(arg, data)) : [];
    return op(args, opNode.sep);
  }

  return node;
}

export function applyComputeMap(computeMap: ComputeMap = {}, values: ComputeData = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!computeMap || typeof computeMap !== 'object') return out;
  for (const [key, expr] of Object.entries(computeMap)) {
    try {
      out[key] = evaluateCompute(expr, values);
    } catch {
      out[key] = undefined;
    }
  }
  return out;
}

export default { evaluateCompute, applyComputeMap };
